import { consoleEvents, LoadingStates } from '@pb/utils'
import { values } from 'ramda'
import { createReducer } from 'redux-act'

import { METHODS, STATUS } from '../constants'
import { PaymentCard } from '../types/payments'
import actions from './actions'
import initialState from './initialState'
import {
  PaymentsError,
  UserSettingsType,
  AddressProp,
  BillingAddressProps,
  NormalizedAdressProps,
  ShippingAddressesProps,
} from './types'

const { GET, CREATE, VERIFY, UPDATE, DELETE } = METHODS
const { LOADING, SUCCESS, FAILURE } = STATUS

const resendEmailConfirmationSuccess = (state): UserSettingsType => ({
  ...state,
  showEmailConfirmationSuccessNotification: true,
})

const resendEmailConfirmationFail = (state): UserSettingsType => ({
  ...state,
  showEmailConfirmationFailureNotification: true,
})

const resendEmailConfirmationReset = (state): UserSettingsType => ({
  ...state,
  showEmailConfirmationFailureNotification: false,
  showEmailConfirmationSuccessNotification: false,
})

const updateSettingsSuccess = (state, message): UserSettingsType => ({
  ...state,
  showSettingsUpdateSuccessNotification: true,
  settingsUpdateNotificationMessage: message || '',
})

const updateSettingsFail = (state, message): UserSettingsType => ({
  ...state,
  showSettingsUpdateFailureNotification: true,
  settingsUpdateNotificationMessage: message || '',
})

const settingsUpdateMessageReset = (state): UserSettingsType => ({
  ...state,
  showSettingsUpdateFailureNotification: false,
  showSettingsUpdateSuccessNotification: false,
  settingsUpdateNotificationMessage: '',
})

const deactivatePayLaterStart = (state): UserSettingsType => ({
  ...state,
  payLaterDeactivate: {
    ...state.payLaterDeactivate,
    isLoading: true,
  },
})

const deactivatePayLaterSuccess = (state): UserSettingsType => ({
  ...state,
  payLaterDeactivate: {
    ...state.payLaterDeactivate,
    isLoading: false,
  },
})

const deactivatePayLaterFail = (state): UserSettingsType => ({
  ...state,
  payLaterDeactivate: {
    ...state.payLaterDeactivate,
    isLoading: false,
    hasFailed: true,
  },
})

const changePasswordStart = (state): UserSettingsType => ({
  ...state,
  changePassword: {
    ...state.changePassword,
    isLoading: true,
    hasFailed: false,
    errors: {},
  },
})

const changePasswordFail = (state, errors): UserSettingsType => ({
  ...state,
  changePassword: {
    ...state.changePassword,
    isLoading: false,
    hasFailed: true,
    errors,
  },
})

const changePasswordSuccess = (state): UserSettingsType => ({
  ...state,
  changePassword: {
    ...state.changePassword,
    isLoading: false,
    hasFailed: false,
    errors: {},
  },
})

const changePasswordResetErrors = (state): UserSettingsType => ({
  ...state,
  changePassword: {
    ...state.changePassword,
    errors: {},
  },
})

const deleteAccountStart = (state): UserSettingsType => ({
  ...state,
  deleteAccount: {
    isLoading: true,
    hasFailed: false,
    error: {},
  },
})

const deleteAccountSuccess = (state): UserSettingsType => ({
  ...state,
  deleteAccount: {
    isLoading: false,
    hasFailed: false,
    success: true,
  },
})

const deleteAccountFail = (state, error): UserSettingsType => ({
  ...state,
  deleteAccount: {
    isLoading: false,
    hasFailed: true,
    error,
  },
})

const deleteAccountSettingsFail = (state, message): UserSettingsType => ({
  ...state,
  showSettingsDeleteFailureNotification: true,
  settingsDeleteNotificationMessage: message || '',
})

const updateUserDetailsStart = (state): UserSettingsType => ({
  ...state,
  updateUserDetails: {
    isLoading: true,
    hasFailed: false,
    errors: false,
  },
})

const updateUserDetailsSuccess = (state): UserSettingsType => ({
  ...state,
  showCompleteProfileReminder: false,
  updateUserDetails: {
    isLoading: false,
    hasFailed: false,
  },
})

const updateUserDetailsFail = (state, errors): UserSettingsType => ({
  ...state,
  updateUserDetails: {
    isLoading: false,
    hasFailed: true,
    errors,
  },
})

const resetCommunityNickname = (state: UserSettingsType): UserSettingsType => ({
  ...state,
  updateCommunityNickname: {
    ...state?.updateCommunityNickname,
    status: LoadingStates.NotLoaded,
    redirectUrl: undefined,
  },
})

const updateCommunityNicknameStart = (state): UserSettingsType => ({
  ...state,
  updateCommunityNickname: {
    status: LoadingStates.Loading,
  },
})

const updateCommunityNicknameSuccess = (
  state: UserSettingsType,
  {
    communityNickname,
    redirectUrl,
  }: Pick<UserSettingsType['updateCommunityNickname'], 'communityNickname' | 'redirectUrl'> = {},
): UserSettingsType => ({
  ...state,
  updateCommunityNickname: {
    status: LoadingStates.LoadedSuccess,
    communityNickname,
    redirectUrl,
  },
})

const updateCommunityNicknameFail = (state: UserSettingsType): UserSettingsType => ({
  ...state,
  updateCommunityNickname: {
    status: LoadingStates.LoadedFail,
  },
})

const getAltIdsStart = (state): UserSettingsType => ({
  ...state,
  alternativeId: {
    ...state.alternativeId,
    method: 'GET',
    status: LoadingStates.Loading,
  },
})

const getAltIdsSuccess = (state, items): UserSettingsType => ({
  ...state,
  alternativeId: {
    ...state.alternativeId,
    items,
    status: LoadingStates.LoadedSuccess,
  },
})

const getAltIdsFail = (state, errors): UserSettingsType => ({
  ...state,
  alternativeId: {
    ...state.alternativeId,
    status: LoadingStates.LoadedFail,
    errors,
  },
})

const addAltIdStart = (state): UserSettingsType => ({
  ...state,
  alternativeId: {
    ...state.alternativeId,
    method: 'ADD',
    status: LoadingStates.Loading,
  },
})

const addAltIdSuccess = (state): UserSettingsType => ({
  ...state,
  alternativeId: {
    ...state.alternativeId,
    status: LoadingStates.LoadedSuccess,
  },
})

const addAltIdFail = (state, errors): UserSettingsType => ({
  ...state,
  alternativeId: {
    ...state.alternativeId,
    status: LoadingStates.LoadedFail,
    errors,
  },
})

const deleteAltIdStart = (state): UserSettingsType => ({
  ...state,
  alternativeId: {
    ...state.alternativeId,
    method: 'DELETE',
    status: LoadingStates.Loading,
  },
})

const deleteAltIdSuccess = (state): UserSettingsType => ({
  ...state,
  alternativeId: {
    ...state.alternativeId,
    status: LoadingStates.LoadedSuccess,
  },
})

const deleteAltIdFail = (state, errors): UserSettingsType => ({
  ...state,
  alternativeId: {
    ...state.alternativeId,
    status: LoadingStates.LoadedFail,
    errors,
  },
})

const updateApiErrorState = (state, errorState: boolean): UserSettingsType => ({
  ...state,
  isApiError: errorState,
})

const addressBookGet = (state, payload): UserSettingsType => ({
  ...state,
  ...payload,
})

const addressBookCreateUpdate = (state, address: AddressProp, action): UserSettingsType => {
  const { billingAddress, defaultAddress: shouldSortNewAddressFirst } = address

  const getBillingAddress = (): BillingAddressProps => ({ billingAddress: address })

  const getShippingAddresses = (): ShippingAddressesProps => {
    const reducers = {
      [CREATE]: () => [...state.shippingAddresses, address],
      [UPDATE]: () => state.shippingAddresses.map((old) => (address.id === old.id ? address : old)),
    }
    const unsorted = (): NormalizedAdressProps[] => reducers[action]()
    const sorted = (): AddressProp[] => [
      address,
      ...unsorted().filter((old) => old.id !== address.id),
    ]
    return { shippingAddresses: shouldSortNewAddressFirst ? sorted() : unsorted() }
  }

  const addresses = billingAddress ? getBillingAddress() : getShippingAddresses()

  return {
    ...state,
    ...addresses,
    verifiedAddresses: initialState.addressBook.verifiedAddresses,
  }
}

const addressBookVerify = (state, verifiedAddresses): UserSettingsType => ({
  ...state,
  verifiedAddresses,
})

const addressBookDelete = (state, addressId): UserSettingsType => ({
  ...state,
  shippingAddresses: state.shippingAddresses.filter((address) => address.id !== addressId),
})

const addressBookDefault = (state): UserSettingsType => ({
  ...state,
})

const addressBookStatus = (state, errors, status): UserSettingsType => {
  const addressBookStatusState = {
    [LOADING]: { loading: true },
    [SUCCESS]: { loading: false, success: true, errors: false },
    [FAILURE]: { loading: false, success: false, errors },
  }

  return {
    ...state,
    ...addressBookStatusState[status],
  }
}

const addressBook = (state, payload, status): UserSettingsType => {
  const reducers = {
    [GET]: addressBookGet,
    [CREATE]: addressBookCreateUpdate,
    [UPDATE]: addressBookCreateUpdate,
    [VERIFY]: addressBookVerify,
    [DELETE]: addressBookDelete,
  }

  const { data, method } = payload

  let addressBookReducer = addressBookDefault
  if (status === SUCCESS) {
    // @ts-expect-error ts-migrate(2322) FIXME: Type '(state: any, address: any, action: any) => a... Remove this comment to see the full error message
    addressBookReducer = reducers[method]
  }

  return {
    ...state,
    addressBook: {
      // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 3.
      ...addressBookReducer(state.addressBook, data, method),
      status: addressBookStatus(state.addressBook.status, data, status),
    },
  }
}

const fetchCitiesStart = (state): UserSettingsType => ({
  ...state,
  addressBook: { ...state?.addressBook, citiesLoadingState: LoadingStates.Loading },
})

const fetchCitiesSuccess = (state, { province, citiesList, provinceCode }): UserSettingsType => ({
  ...state,
  addressBook: {
    ...state?.addressBook,
    cities: {
      ...state?.addressBook?.cities,
      ...(citiesList && { [province]: citiesList }),
    },
    selectedProvinceCode: provinceCode,
    postalCode: '',
    citiesLoadingState: LoadingStates.LoadedSuccess,
  },
})

const fetchCitiesFail = (state, { message }): UserSettingsType => {
  consoleEvents.error('Could not fetch cities: ', message)
  return {
    ...state,
    addressBook: { ...state?.addressBook, citiesLoadingState: LoadingStates.LoadedFail },
  }
}

const fetchDistrictsStart = (state): UserSettingsType => ({
  ...state,
  addressBook: { ...state?.addressBook, districtsLoadingState: LoadingStates.Loading },
})

const fetchDistrictsSuccess = (state, { city, districtsList }): UserSettingsType => ({
  ...state,
  addressBook: {
    ...state?.addressBook,
    districts: {
      ...state?.addressBook?.districts,
      ...(districtsList && { [city]: districtsList }),
    },
    districtsLoadingState: LoadingStates.LoadedSuccess,
  },
})

const fetchDistrictsFail = (state, { message }): UserSettingsType => {
  consoleEvents.error('Could not fetch districts: ', message)
  return {
    ...state,
    addressBook: { ...state?.addressBook, districtsLoadingState: LoadingStates.LoadedFail },
  }
}

const fetchPostalCodeStart = (state): UserSettingsType => ({
  ...state,
  addressBook: { ...state?.addressBook, postalCodeLoadingState: LoadingStates.Loading },
})

const fetchPostalCodeSuccess = (state, postalCode): UserSettingsType => ({
  ...state,
  addressBook: {
    ...state?.addressBook,
    postalCode,
    postalCodeLoadingState: LoadingStates.LoadedSuccess,
  },
})

const fetchPostalCodeFail = (state, { message }): UserSettingsType => {
  consoleEvents.error('Could not fetch postal code: ', message)
  return {
    ...state,
    addressBook: { ...state?.addressBook, postalCodeLoadingState: LoadingStates.LoadedFail },
  }
}

const setSelectedProvince = (state, { province, provinceCode }): UserSettingsType => {
  return {
    ...state,
    addressBook: {
      ...state?.addressBook,
      selectedProvince: province,
      selectedProvinceCode: provinceCode,
    },
  }
}

const setSelectedCity = (state, city): UserSettingsType => ({
  ...state,
  addressBook: { ...state?.addressBook, selectedCity: city },
})

// Payments
const fetchPaymentsStart = (state: UserSettingsType): UserSettingsType => ({
  ...state,
  payments: { ...state?.payments, paymentsLoadingState: LoadingStates.Loading },
})

const fetchPaymentsSuccess = (
  state: UserSettingsType,
  { context: payments },
): UserSettingsType => ({
  ...state,
  payments: {
    ...state?.payments,
    ...payments,
    showBankAccountForm: !(
      payments?.bankAccountDetail?.bankaccountname || payments?.bankAccountDetail?.IBAN
    ),
    paymentsLoadingState: LoadingStates.LoadedSuccess,
  },
})

const fetchPaymentsFail = (state: UserSettingsType, error: Error): UserSettingsType => {
  consoleEvents.error(`Failed fetch payments:`, error)

  return {
    ...state,
    payments: { ...state?.payments, paymentsLoadingState: LoadingStates.LoadedFail },
  }
}

const fetchBranchStart = (state): UserSettingsType => ({
  ...state,
  payments: { ...state?.payments },
})

const fetchBranchSuccess = (state, { branchList }): UserSettingsType => ({
  ...state,
  payments: {
    ...state?.payments,
    branchList: {
      ...branchList,
    },
  },
})

const fetchBranchFail = (state, { message }): UserSettingsType => {
  consoleEvents.error('Could not fetch branches: ', message)
  return {
    ...state,
    payments: { ...state?.payments },
  }
}

const removeCardStart = (state: UserSettingsType): UserSettingsType => {
  return {
    ...state,
    payments: {
      ...state?.payments,
      removePaymentLoadingState: LoadingStates.Loading,
    },
  }
}

const removeCardSuccess = (state: UserSettingsType): UserSettingsType => ({
  ...state,
  payments: {
    ...state?.payments,
    removePaymentLoadingState: LoadingStates.LoadedSuccess,
  },
})

const removeCardFail = (
  state: UserSettingsType,
  { previousPaymentsState }: PaymentsError,
): UserSettingsType => {
  return {
    ...state,
    payments: {
      ...previousPaymentsState,
      removePaymentLoadingState: LoadingStates.LoadedFail,
    },
  }
}

const removeBankAccountStart = (state: UserSettingsType): UserSettingsType => {
  return {
    ...state,
    payments: {
      ...state?.payments,

      removePaymentLoadingState: LoadingStates.Loading,
    },
  }
}

const removeBankAccountSuccess = (state: UserSettingsType): UserSettingsType => ({
  ...state,
  payments: {
    ...state?.payments,
    removePaymentLoadingState: LoadingStates.LoadedSuccess,
  },
})

const removeBankAccountFail = (
  state: UserSettingsType,
  { error, previousPaymentsState }: PaymentsError,
): UserSettingsType => {
  consoleEvents.error(`Failed remove card:`, error)

  return {
    ...state,
    payments: {
      ...previousPaymentsState,
      removePaymentLoadingState: LoadingStates.LoadedFail,
    },
  }
}

const setDefaultCardStart = (state: UserSettingsType, cardId: string): UserSettingsType => {
  const cards = state?.payments?.paymentInfoData?.map((card) => {
    if (card.id === cardId) {
      return {
        ...card,
        defaultPaymentInfo: true,
      }
    }

    return {
      ...card,
      defaultPaymentInfo: false,
    }
  })
  return {
    ...state,
    payments: {
      ...state?.payments,
      paymentInfoData: cards,
      defaultCardConfig: {
        selectedId: cardId,
        loadingState: LoadingStates.Loading,
      },
    },
  }
}

const setDefaultCardSuccess = (state: UserSettingsType, cardId: string): UserSettingsType => ({
  ...state,
  payments: {
    ...state?.payments,
    defaultCardConfig: {
      selectedId: cardId,
      loadingState: LoadingStates.LoadedSuccess,
    },
  },
})

const setDefaultCardFail = (
  state: UserSettingsType,
  { error, previousPaymentsState }: PaymentsError,
): UserSettingsType => {
  consoleEvents.error(`Failed remove card:`, error)

  return {
    ...state,
    payments: {
      ...previousPaymentsState,
    },
  }
}

const triggerNotification = (state: UserSettingsType, paymentsNotification): UserSettingsType => ({
  ...state,
  payments: { ...state?.payments, paymentsNotification },
})

const loadCardStoreIds = (state, cards: PaymentCard[]): UserSettingsType => ({
  ...state,
  payments: {
    ...state?.payments,
    paymentCardMemberIds: cards.length
      ? cards.map((card) => ({ alias: card.alias, state: LoadingStates.NotLoaded }))
      : [],
  },
})

const addPaymentCardMemberIdStart = (state: UserSettingsType, alias: string): UserSettingsType => ({
  ...state,
  payments: {
    ...state?.payments,
    paymentCardMemberIds: state?.payments.paymentCardMemberIds.map((pcmi) =>
      pcmi.alias === alias
        ? { ...pcmi, state: LoadingStates.Loading, errorMessage: undefined }
        : pcmi,
    ),
  },
})

const addPaymentCardMemberIdSuccess = (
  state: UserSettingsType,
  alias: string,
): UserSettingsType => ({
  ...state,
  payments: {
    ...state?.payments,
    paymentCardMemberIds: state?.payments.paymentCardMemberIds.map((pcmi) =>
      pcmi.alias === alias ? { ...pcmi, state: LoadingStates.LoadedSuccess } : pcmi,
    ),
    paymentInfoData: state?.payments.paymentInfoData?.map((card) =>
      card.alias === alias ? { ...card, useAsId: true } : card,
    ),
  },
})

const addPaymentCardMemberIdFail = (
  state: UserSettingsType,
  { alias, errorMessage },
): UserSettingsType => ({
  ...state,
  payments: {
    ...state?.payments,
    paymentCardMemberIds: state?.payments.paymentCardMemberIds.map((pcmi) =>
      pcmi.alias === alias ? { ...pcmi, state: LoadingStates.LoadedFail, errorMessage } : pcmi,
    ),
  },
})

const deletePaymentCardMemberIdStart = (
  state: UserSettingsType,
  alias: string,
): UserSettingsType => ({
  ...state,
  payments: {
    ...state?.payments,
    paymentCardMemberIds: state?.payments.paymentCardMemberIds.map((pcmi) =>
      pcmi.alias === alias ? { ...pcmi, state: LoadingStates.Loading } : pcmi,
    ),
  },
})

const deletePaymentCardMemberIdSuccess = (
  state: UserSettingsType,
  alias: string,
): UserSettingsType => ({
  ...state,
  payments: {
    ...state?.payments,
    paymentCardMemberIds: state?.payments.paymentCardMemberIds.map((pcmi) =>
      pcmi.alias === alias ? { ...pcmi, state: LoadingStates.LoadedSuccess } : pcmi,
    ),
    paymentInfoData: state?.payments.paymentInfoData?.map((card) =>
      card.alias === alias ? { ...card, useAsId: false } : card,
    ),
  },
})

const deletePaymentCardMemberIdFail = (
  state: UserSettingsType,
  alias: string,
): UserSettingsType => ({
  ...state,
  payments: {
    ...state?.payments,
    paymentCardMemberIds: state?.payments.paymentCardMemberIds.map((pcmi) =>
      pcmi.alias === alias ? { ...pcmi, state: LoadingStates.LoadedFail } : pcmi,
    ),
  },
})

const addBankAccountStart = (state: UserSettingsType): UserSettingsType => ({
  ...state,
  payments: {
    ...state?.payments,
    status: LoadingStates.Loading,
  },
})

const addBankAccountSuccess = (
  state: UserSettingsType,
  { context: payments },
): UserSettingsType => ({
  ...state,
  payments: {
    ...state?.payments,
    ...payments,
    showBankAccountForm: !(
      payments?.bankAccountDetail?.bankaccountname || payments?.bankAccountDetail?.IBAN
    ),
    status: LoadingStates.LoadedSuccess,
  },
})

const addBankAccountFail = (state: UserSettingsType): UserSettingsType => {
  return {
    ...state,
    payments: {
      ...state?.payments,
      status: LoadingStates.LoadedFail,
    },
  }
}

const updateBankAccountDetailsFail = (state, errors): UserSettingsType => ({
  ...state,
  payments: {
    ...state?.payments,
    status: LoadingStates.LoadedFail,
    errors,
  },
})

const updateBankAccountErrorState = (state): UserSettingsType => ({
  ...state,
  payments: {
    ...state?.payments,
    errors: {},
  },
})
const addNewPaymentCardStart = (state: UserSettingsType): UserSettingsType => ({
  ...state,
  payments: {
    ...state?.payments,
    addNewCard: {
      status: LoadingStates.Loading,
      errors: [],
    },
  },
})

const addNewPaymentCardSuccess = (state: UserSettingsType): UserSettingsType => ({
  ...state,
  payments: {
    ...state?.payments,
    addNewCard: {
      status: LoadingStates.LoadedSuccess,
      errors: [],
    },
  },
})

const addNewPaymentCardFail = (
  state: UserSettingsType,
  errors: UserSettingsType['payments']['addNewCard']['errors'],
): UserSettingsType => ({
  ...state,
  payments: {
    ...state?.payments,
    addNewCard: {
      status: LoadingStates.LoadedFail,
      errors: errors || [],
    },
  },
})

const resetNewPaymentCardForm = (state: UserSettingsType): UserSettingsType => ({
  ...state,
  payments: {
    ...state?.payments,
    addNewCard: {
      status: LoadingStates.NotLoaded,
      errors: [],
    },
  },
})

const setNewsSubscription = (
  state: UserSettingsType,
  newsLetterSubscription: boolean,
): UserSettingsType => ({
  ...state,
  newsLetterSubscription,
})

const addPhoneAsAltIdSuccess = (state): UserSettingsType => ({
  ...state,
  showPhoneAsAltIdAddedNotification: true,
  user: {
    ...state.user,
    altIdSaved: true,
  },
})

const removePhoneAsAltIdSuccess = (state): UserSettingsType => ({
  ...state,
  showPhoneAsAltIdRemovedNotification: true,
  user: {
    ...state.user,
    altIdSaved: false,
  },
})

const resetPhoneAsAltIdNotification = (state): UserSettingsType => ({
  ...state,
  showPhoneAsAltIdAddedNotification: false,
  showPhoneAsAltIdRemovedNotification: false,
})

const reducer = createReducer((on) => {
  // Notifications
  // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'EmptyActionCreator[]' is not ass... Remove this comment to see the full error message
  on(actions.settingsUpdateFailure, updateSettingsFail)
  // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'EmptyActionCreator[]' is not ass... Remove this comment to see the full error message
  on(actions.settingsUpdateSuccess, updateSettingsSuccess)
  on(actions.resendEmailConfirmationSuccess, resendEmailConfirmationSuccess)
  on(actions.resendEmailConfirmationFail, resendEmailConfirmationFail)
  on(actions.resendEmailConfirmationReset, resendEmailConfirmationReset)
  on(actions.settingsUpdateMessageReset, settingsUpdateMessageReset)
  on(actions.setApiErrorState, updateApiErrorState)

  // Pay Later
  on(actions.deactivatePayLaterStart, deactivatePayLaterStart)
  on(actions.deactivatePayLaterSuccess, deactivatePayLaterSuccess)
  on(actions.deactivatePayLaterFail, deactivatePayLaterFail)

  // Update User
  on(actions.updateUserDetailsStart, updateUserDetailsStart)
  on(actions.updateUserDetailsSuccess, updateUserDetailsSuccess)
  on(actions.updateUserDetailsFail, updateUserDetailsFail)

  // Update community nickname
  on(actions.resetCommunityNickname, resetCommunityNickname)
  on(actions.updateCommunityNicknameStart, updateCommunityNicknameStart)
  on(actions.updateCommunityNicknameSuccess, updateCommunityNicknameSuccess)
  on(actions.updateCommunityNicknameFail, updateCommunityNicknameFail)

  // Alternative ID
  on(actions.getAltIdsStart, getAltIdsStart)
  on(actions.getAltIdsSuccess, getAltIdsSuccess)
  on(actions.getAltIdsFail, getAltIdsFail)
  on(actions.addAltIdStart, addAltIdStart)
  on(actions.addAltIdSuccess, addAltIdSuccess)
  on(actions.addAltIdFail, addAltIdFail)
  on(actions.deleteAltIdStart, deleteAltIdStart)
  on(actions.deleteAltIdSuccess, deleteAltIdSuccess)
  on(actions.deleteAltIdFail, deleteAltIdFail)

  // AddressBook
  // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'any[]' is not assignable to para... Remove this comment to see the full error message
  on(values(actions.addressBook.LOADING), (state, payload) => addressBook(state, payload, LOADING))
  // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'any[]' is not assignable to para... Remove this comment to see the full error message
  on(values(actions.addressBook.VERIFY), (state, payload) => addressBook(state, payload, SUCCESS))
  // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'any[]' is not assignable to para... Remove this comment to see the full error message
  on(values(actions.addressBook.SUCCESS), (state, payload) => addressBook(state, payload, SUCCESS))
  // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'any[]' is not assignable to para... Remove this comment to see the full error message
  on(values(actions.addressBook.FAILURE), (state, payload) => addressBook(state, payload, FAILURE))

  // Change Password
  on(actions.changePasswordStart, changePasswordStart)
  on(actions.changePasswordSuccess, changePasswordSuccess)
  on(actions.changePasswordFail, changePasswordFail)
  on(actions.changePasswordResetErrors, changePasswordResetErrors)

  // Delete Account
  on(actions.deleteAccountStart, deleteAccountStart)
  on(actions.deleteAccountSuccess, deleteAccountSuccess)
  on(actions.deleteAccountFail, deleteAccountFail)
  // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'EmptyActionCreator[]' is not ass... Remove this comment to see the full error message
  on(actions.settingsDeleteAccountFailure, deleteAccountSettingsFail)

  // Cities , Districts and PostalCode
  on(actions.setSelectedProvince, setSelectedProvince)
  on(actions.setSelectedCity, setSelectedCity)
  on(actions.fetchCitiesStart, fetchCitiesStart)
  on(actions.fetchCitiesSuccess, fetchCitiesSuccess)
  on(actions.fetchCitiesFail, fetchCitiesFail)
  on(actions.fetchDistrictsStart, fetchDistrictsStart)
  on(actions.fetchDistrictsSuccess, fetchDistrictsSuccess)
  on(actions.fetchDistrictsFail, fetchDistrictsFail)
  on(actions.fetchPostalCodeStart, fetchPostalCodeStart)
  on(actions.fetchPostalCodeSuccess, fetchPostalCodeSuccess)
  on(actions.fetchPostalCodeFail, fetchPostalCodeFail)

  // Payments
  on(actions.fetchPaymentsStart, fetchPaymentsStart)
  on(actions.fetchPaymentsSuccess, fetchPaymentsSuccess)
  on(actions.fetchPaymentsFail, fetchPaymentsFail)
  on(actions.removeCardStart, removeCardStart)
  on(actions.removeCardSuccess, removeCardSuccess)
  on(actions.removeCardFail, removeCardFail)
  on(actions.addBankAccountStart, addBankAccountStart)
  on(actions.addBankAccountSuccess, addBankAccountSuccess)
  on(actions.addBankAccountFail, addBankAccountFail)
  on(actions.updateBankAccountDetailsFail, updateBankAccountDetailsFail)
  on(actions.updateBankAccountErrorState, updateBankAccountErrorState)

  on(actions.removeBankAccountStart, removeBankAccountStart)
  on(actions.removeBankAccountSuccess, removeBankAccountSuccess)
  on(actions.removeBankAccountFail, removeBankAccountFail)

  on(actions.setDefaultCardStart, setDefaultCardStart)
  on(actions.setDefaultCardSuccess, setDefaultCardSuccess)
  on(actions.setDefaultCardFail, setDefaultCardFail)

  on(actions.triggerNotification, triggerNotification)
  on(actions.loadCardStoreIds, loadCardStoreIds)
  on(actions.addPaymentCardMemberIdStart, addPaymentCardMemberIdStart)
  on(actions.addPaymentCardMemberIdSuccess, addPaymentCardMemberIdSuccess)
  on(actions.addPaymentCardMemberIdFail, addPaymentCardMemberIdFail)
  on(actions.deletePaymentCardMemberIdStart, deletePaymentCardMemberIdStart)
  on(actions.deletePaymentCardMemberIdSuccess, deletePaymentCardMemberIdSuccess)
  on(actions.deletePaymentCardMemberIdFail, deletePaymentCardMemberIdFail)

  on(actions.addNewPaymentCardStart, addNewPaymentCardStart)
  on(actions.addNewPaymentCardSuccess, addNewPaymentCardSuccess)
  on(actions.addNewPaymentCardFail, addNewPaymentCardFail)
  on(actions.resetNewPaymentCardForm, resetNewPaymentCardForm)
  on(actions.setNewsSubscription, setNewsSubscription)
  on(actions.fetchBranchStart, fetchBranchStart)
  on(actions.fetchBranchSuccess, fetchBranchSuccess)
  on(actions.fetchBranchFail, fetchBranchFail)
  on(actions.addPhoneAsAltIdSuccess, addPhoneAsAltIdSuccess)
  on(actions.removePhoneAsAltIdSuccess, removePhoneAsAltIdSuccess)
  on(actions.resetPhoneAsAltIdNotification, resetPhoneAsAltIdNotification)
}, initialState)

export default reducer
